import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Eye,
  UploadCloud,
  Sparkles,
  ShieldCheck,
  Settings2,
  BarChart3,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

// ─── Reusable toggle ───────────────────────────────────────────────
function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
      style={{ background: checked ? 'var(--a)' : 'var(--b2)' }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full transition-transform"
        style={{ left: 2, background: 'var(--white)', transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
      />
    </button>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div
      className="flex items-center justify-between py-2.5"
      style={{ borderBottom: '1px solid var(--b1)' }}
    >
      <div className="pr-3 min-w-0">
        <p className="font-body text-sm" style={{ color: 'var(--white)' }}>{label}</p>
        {hint && <p className="font-body text-xs mt-0.5" style={{ color: 'var(--ts)' }}>{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} testId={testId} />
    </div>
  );
}

function SectionCard({ kicker, title, icon: Icon, children, testId }) {
  return (
    <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid={testId}>
      <div className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {kicker}
        </p>
        <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
          {Icon && <Icon size={14} className="inline -mt-0.5 mr-1.5" style={{ color: 'var(--a)' }} />}
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function LabelledInput({ label, testId, value, onChange, placeholder, textarea, rows, type = 'text', maxLength }) {
  const Cmp = textarea ? 'textarea' : Input;
  const baseClass = textarea
    ? 'w-full font-mono text-sm rounded-[3px] p-2 resize-none'
    : 'font-mono text-sm h-11 rounded-[3px]';
  return (
    <div>
      <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>{label}</label>
      <Cmp
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        type={type}
        maxLength={maxLength}
        className={baseClass}
        style={INPUT_STYLE}
      />
    </div>
  );
}

const HERO_FIELDS = [
  ['headline_line_1',    'Headline Line 1',    'Grow Faster.'],
  ['headline_line_2',    'Headline Line 2',    'Stay Secure.'],
  ['headline_line_3',    'Headline Line 3',    'Be Found.'],
  ['primary_cta_label',  'Primary CTA Label',  'Explore Courses'],
  ['secondary_cta_label','Secondary CTA Label','Talk to Us'],
];

// ─── Main section ──────────────────────────────────────────────────
export default function SiteContentSection() {
  const { adminFetch } = useAdminAuth();
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/site-content`);
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const { site_content } = await res.json();
      // Drop updated_at from the draft shape so it doesn't cloud dirty detection
      const clean = { ...site_content };
      delete clean.updated_at;
      setDraft(clean);
      setOriginal(clean);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const isDirty = useMemo(
    () => draft && original && JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original],
  );

  const handlePreview = () => {
    toast.info('Opening live preview...', {
      description: 'A browser tab would open to /?preview=1 in production.',
    });
  };

  const handlePublish = async () => {
    if (!isDirty) return;
    setPublishing(true);
    setError(null);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/site-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Publish failed: ${res.status}`);
      }
      const { site_content, changed_fields } = await res.json();
      const clean = { ...site_content };
      delete clean.updated_at;
      setDraft(clean);
      setOriginal(clean);
      toast.success('Changes published to live site', {
        description: `${changed_fields} field${changed_fields === 1 ? '' : 's'} updated.`,
      });
    } catch (err) {
      setError(err.message);
      toast.error('Publish failed', { description: err.message });
    } finally {
      setPublishing(false);
    }
  };

  // Helper setters — pathed updates
  const setHero = (k, v) => setDraft((d) => ({ ...d, hero: { ...d.hero, [k]: v } }));
  const setTrustToggle = (k, v) => setDraft((d) => ({ ...d, trust_bar: { ...d.trust_bar, [k]: v } }));
  const setBadge = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      trust_bar: {
        ...d.trust_bar,
        badges: d.trust_bar.badges.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)),
      },
    }));
  const setSiteSetting = (k, v) =>
    setDraft((d) => ({ ...d, site_settings: { ...d.site_settings, [k]: v } }));
  const setStat = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      stats_band: d.stats_band.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  if (loading) {
    return (
      <div className="p-8 rounded-lg" style={CARD_STYLE}>
        <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading site content…</p>
      </div>
    );
  }
  if (!draft) return null;

  return (
    <div className="space-y-5" data-testid="admin-site-content-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · site-content
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
            Site Content
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {isDirty && (
            <span
              data-testid="admin-site-content-dirty"
              className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded"
              style={{ color: 'var(--warn)', background: 'var(--warn-10)' }}
            >
              unsaved
            </span>
          )}
          <Button
            data-testid="admin-site-content-preview"
            onClick={handlePreview}
            className="font-body text-sm h-10 px-4 rounded-[3px]"
            style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
          >
            <Eye size={14} className="mr-1.5" />
            Preview Site
          </Button>
          <Button
            data-testid="admin-site-content-publish"
            onClick={handlePublish}
            disabled={!isDirty || publishing}
            className="btn-shimmer font-body text-sm h-10 px-4 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: !isDirty || publishing ? 0.5 : 1 }}
          >
            <UploadCloud size={14} className="mr-1.5" />
            {publishing ? 'Publishing…' : 'Publish Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      {/* Hero */}
      <SectionCard kicker="hero" title="Hero Section" icon={Sparkles} testId="admin-site-content-hero">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          {HERO_FIELDS.slice(0, 3).map(([k, label, ph]) => (
            <LabelledInput
              key={k}
              label={label}
              testId={`admin-hero-${k}`}
              value={draft.hero[k]}
              onChange={(v) => setHero(k, v)}
              placeholder={ph}
              maxLength={120}
            />
          ))}
        </div>
        <div className="mb-3">
          <LabelledInput
            label="Sub-headline"
            testId="admin-hero-sub_headline"
            value={draft.hero.sub_headline}
            onChange={(v) => setHero('sub_headline', v)}
            placeholder="Sub-headline copy"
            textarea
            rows={4}
            maxLength={1000}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {HERO_FIELDS.slice(3).map(([k, label, ph]) => (
            <LabelledInput
              key={k}
              label={label}
              testId={`admin-hero-${k}`}
              value={draft.hero[k]}
              onChange={(v) => setHero(k, v)}
              placeholder={ph}
              maxLength={60}
            />
          ))}
        </div>
      </SectionCard>

      {/* Trust bar */}
      <SectionCard kicker="trust" title="Trust Bar" icon={ShieldCheck} testId="admin-site-content-trust">
        <div className="space-y-3 mb-4">
          {draft.trust_bar.badges.map((b, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LabelledInput
                label={`Badge ${i + 1} · Title`}
                testId={`admin-trust-badge-${i}-title`}
                value={b.title}
                onChange={(v) => setBadge(i, 'title', v)}
                placeholder="Title"
                maxLength={80}
              />
              <LabelledInput
                label={`Badge ${i + 1} · Subtitle`}
                testId={`admin-trust-badge-${i}-subtitle`}
                value={b.subtitle}
                onChange={(v) => setBadge(i, 'subtitle', v)}
                placeholder="Subtitle"
                maxLength={160}
              />
            </div>
          ))}
        </div>
        <ToggleRow
          label="Show Trust Bar"
          hint="Display the trust bar row on the homepage."
          checked={draft.trust_bar.show_trust_bar}
          onChange={(v) => setTrustToggle('show_trust_bar', v)}
          testId="admin-trust-toggle-show"
        />
        <ToggleRow
          label="Show Cyber Essentials Badge"
          hint="Add the Cyber Essentials certification mark."
          checked={draft.trust_bar.show_cyber_essentials}
          onChange={(v) => setTrustToggle('show_cyber_essentials', v)}
          testId="admin-trust-toggle-cyber"
        />
      </SectionCard>

      {/* Site-Wide Settings */}
      <SectionCard kicker="global" title="Site-Wide Settings" icon={Settings2} testId="admin-site-content-settings">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Column 1 */}
          <div>
            <ToggleRow
              label="Maintenance Mode"
              checked={draft.site_settings.maintenance_mode}
              onChange={(v) => setSiteSetting('maintenance_mode', v)}
              testId="admin-settings-maintenance"
            />
            <ToggleRow
              label="AI Chat Widget"
              checked={draft.site_settings.ai_chat_widget}
              onChange={(v) => setSiteSetting('ai_chat_widget', v)}
              testId="admin-settings-ai-chat"
            />
            <ToggleRow
              label="Cookie Banner"
              checked={draft.site_settings.cookie_banner}
              onChange={(v) => setSiteSetting('cookie_banner', v)}
              testId="admin-settings-cookie"
            />
          </div>

          {/* Column 2 */}
          <div>
            <ToggleRow
              label="Course Quiz"
              checked={draft.site_settings.course_quiz}
              onChange={(v) => setSiteSetting('course_quiz', v)}
              testId="admin-settings-quiz"
            />
            <ToggleRow
              label="Salary Data Panel"
              checked={draft.site_settings.salary_data_panel}
              onChange={(v) => setSiteSetting('salary_data_panel', v)}
              testId="admin-settings-salary"
            />
            <ToggleRow
              label="Threat Console"
              checked={draft.site_settings.threat_console}
              onChange={(v) => setSiteSetting('threat_console', v)}
              testId="admin-settings-threat"
            />
          </div>

          {/* Column 3 */}
          <div className="space-y-3">
            <LabelledInput
              label="Announcement Banner (blank = hidden)"
              testId="admin-settings-announcement"
              value={draft.site_settings.announcement_banner}
              onChange={(v) => setSiteSetting('announcement_banner', v)}
              placeholder="Early access opens Monday…"
              maxLength={200}
            />
            <LabelledInput
              label="Contact Email"
              testId="admin-settings-contact-email"
              type="email"
              value={draft.site_settings.contact_email}
              onChange={(v) => setSiteSetting('contact_email', v)}
              placeholder="connect@acodia.co.uk"
              maxLength={160}
            />
          </div>
        </div>
      </SectionCard>

      {/* Stats Band */}
      <SectionCard kicker="stats" title="Stats Band" icon={BarChart3} testId="admin-site-content-stats">
        <div className="space-y-3">
          {draft.stats_band.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LabelledInput
                label={`Stat ${i + 1} · Label`}
                testId={`admin-stat-${i}-label`}
                value={s.label}
                onChange={(v) => setStat(i, 'label', v)}
                placeholder="Threats Blocked"
                maxLength={60}
              />
              <LabelledInput
                label={`Stat ${i + 1} · Value`}
                testId={`admin-stat-${i}-value`}
                value={s.value}
                onChange={(v) => setStat(i, 'value', v)}
                placeholder="12,470"
                maxLength={40}
              />
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
